import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';
import startCase from 'lodash/startCase';
import camelCase from 'lodash/camelCase';
import replace from 'lodash/replace';
import { FormattedMessage } from 'react-intl';
import { toMoment } from '@fortress-technology-solutions/fortress-component-library/utils';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import {
  Button,
  Box,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { PlusIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import { HEADERS, PROPERTY_PATH_MAP, STATUSES } from './constants';
import ReportsService from '../../services/reportService';
import ScheduledReportService from '../../services/scheduledReportService';
import generalMessages from '../App/messages';
import componentMessages from './messages';
import useFormatMessage from '../../hooks/useFormatMessage';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useAbortController from '../../hooks/useAbortController';
import { getUserDisplayName } from '../../utils/system-user';

const { IN_PROGRESS, SUCCESSFUL, ERROR, NO_DATA } = STATUSES;
const messages = { ...generalMessages, ...componentMessages };
const reportsService = new ReportsService();
const scheduledReportService = new ScheduledReportService();

export const useManageCentralizedReports = ({
  NAME,
  organizationId,
  userId,
  username,
  onSelectReport,
  openViewHistoryModal,
  newlyGeneratedReports: externalNewlyGeneratedReports,
  setNewlyGeneratedReports: externalSetNewlyGeneratedReports,
}) => {
  const name = useUniqueTableName(NAME);
  const [internalNewlyGeneratedReports, setInternalNewlyGeneratedReports] =
    useState([]);

  // Use external state if provided, otherwise use internal state
  const newlyGeneratedReports =
    externalNewlyGeneratedReports ?? internalNewlyGeneratedReports;
  const setNewlyGeneratedReports =
    externalSetNewlyGeneratedReports ?? setInternalNewlyGeneratedReports;

  // Now reporting-backend returns BOTH legacy and custom reports
  const { results: allReports, isLoading } =
    useFetchCentralizedReportDefinitions({
      organizationId,
    });

  const { results: reportsStatuses } = useFetchReportsStatuses({
    organizationId,
    newlyGeneratedReports,
    setNewlyGeneratedReports,
  });
  const [projectNames, setProjectNames] = useState([]);

  const finalResults = useMemo(
    () =>
      allReports.map((result) => {
        const currentReportStatus = reportsStatuses.find(
          ({ fortressReportId }) => fortressReportId === result.id,
        );
        return {
          ...result,
          status: currentReportStatus?.status,
          userId: currentReportStatus?.requestedBy,
          username: currentReportStatus?.requestedByUsername,
          requestedTime: currentReportStatus?.createdAt,
        };
      }),
    [allReports, reportsStatuses],
  );
  const { statusOptions } = useGetFilterOptions({
    results: finalResults,
  });
  const headers = useHeaders({
    statusOptions,
  });
  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
  });
  const sortedAndFilteredResults = useTableFilterSortData({
    results: finalResults,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  function initializeCreateReport(row) {
    if (onSelectReport) {
      onSelectReport(row);
    }
  }
  async function handleDownload(row) {
    // For new reports, use the report name as filename since they don't have a pre-defined filename
    let filename;
    if (row.version === 'new' && row.name) {
      // New reports: use report name + .zip (will be handled by backend)
      filename = `${row.name.toLowerCase().replace(/\s+/g, '-')}.zip`;
    } else {
      // Legacy reports: extract filename from path
      filename = row.filename?.includes('/')
        ? row.filename.split('/')[1]
        : row.filename;
    }

    await reportsService.downloadCentralizedReport(
      organizationId,
      row.id,
      filename,
      row.version,
    );
  }
  const rows = useRows({
    sortedAndFilteredResults,
    initializeCreateReport,
    handleDownload,
    openViewHistoryModal,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
  });

  useEffect(() => {
    const fetchProjectNames = async () => {
      const response = await reportsService.getProjectNames({ organizationId });
      setProjectNames(response?.projectNames || []);
    };
    fetchProjectNames();
  }, [organizationId]);

  return {
    allColumnsHidden,
    columnOptions,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount: finalResults?.length ?? 0,
    projectNames,
  };
};

export const useManageScheduledReports = ({
  NAME,
  organizationId,
  userId,
  username,
  enabled = true,
  onViewEdit,
}) => {
  const name = useUniqueTableName(NAME);

  const { results, isLoading } = useFetchScheduledReports({
    organizationId,
    userId,
    enabled,
  });

  const headers = useScheduledReportHeaders();

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
  });

  // Transform filterState to map 'report' filter to 'levelType' field
  // This is needed because filterOptions is on 'report' header but filters by 'levelType'
  const transformedFilterState = useMemo(() => {
    if (!filterState.report) return filterState;
    const { report, ...rest } = filterState;
    // Ensure the value is an array for consistent filtering
    const levelTypeValue = Array.isArray(report) ? report : [report];
    return {
      ...rest,
      levelType: levelTypeValue,
    };
  }, [filterState]);

  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState: transformedFilterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP: SCHEDULED_REPORTS_PROPERTY_PATH_MAP,
  });

  const totalCount = sortedAndFilteredResults.length;

  function handleViewEdit(row) {
    if (onViewEdit) {
      onViewEdit(row);
    }
  }

  const rows = useScheduledReportRows({
    sortedAndFilteredResults: sortedAndFilteredResults,
    handleViewEdit,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
  });

  return {
    allColumnsHidden,
    columnOptions,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount,
  };
};

export const useFetchCentralizedReportDefinitions = ({ organizationId }) => {
  const abortControllerOptions = useAbortController();
  const queryKey = ['CentralizedReports', 'definitions', organizationId];
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      reportsService.getCentralizedReportDefinitions({
        options: abortControllerOptions,
        organizationId,
      }),
    {
      enabled: Boolean(organizationId?.length),
      refetchOnWindowFocus: false,
      refetchInterval: 5000,
    },
  );

  return {
    results: useMemo(() => {
      const items = data?.results || data || [];
      if (!Array.isArray(items)) return [];
      return items.map((item) => ({
        id: item.id,
        name: item.name,
        type: item.type || 'Centralized',
        lastCreated: item.lastCreated || null,
        filename: item.filename || '',
        version: item.version || 'legacy',
        body: item.body,
        zip: item.zip,
        organizationId: item.organizationId,
        configuration: item.configuration,
      }));
    }, [data]),
    isLoading,
  };
};

// Faz prefetch dos dados de reports para o modal de agendamento
// quando a pagina carrega, para que o modal abra instantaneamente
export const usePrefetchSchedulingReports = ({
  organizationId,
  enabled = true,
}) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !organizationId) return;

    // Faz prefetch dos dados de reports para o modal de agendamento
    queryClient.prefetchQuery(
      ['CentralizedReports', 'definitions', organizationId, 'all'],
      () =>
        reportsService.getCentralizedReportDefinitions({
          organizationId,
          all: true,
        }),
      {
        staleTime: 5 * 60 * 1000, // 5 minutos - dados considerados frescos
      },
    );
  }, [queryClient, organizationId, enabled]);
};

export const useHeaders = ({ statusOptions }) => {
  return useMemo(() => {
    return HEADERS.map((header) => {
      if (header.id === 'status') header.filterOptions = statusOptions;
      return header;
    });
  }, [statusOptions]);
};

export const useRows = ({
  sortedAndFilteredResults,
  initializeCreateReport,
  handleDownload,
  openViewHistoryModal,
}) => {
  const formatMessage = useFormatMessage();
  return useMemo(
    () =>
      sortedAndFilteredResults.map((row) => {
        const { name, type, lastCreated, status, username, requestedTime } =
          row;
        const isNotError =
          !status || [SUCCESSFUL, IN_PROGRESS].includes(status);
        const isSuccessful = status === SUCCESSFUL;
        const reportExists =
          status === IN_PROGRESS ||
          status === NO_DATA ||
          status === ERROR ||
          lastCreated;
        return {
          reports: {
            value: name,
          },
          type: {
            value: type,
          },
          createdBy: {
            value:
              reportExists &&
              getUserDisplayName({ username }, username, row.userId),
          },
          creationDate: {
            variant: 'date',
            value: reportExists && (isSuccessful ? lastCreated : requestedTime),
          },
          status: {
            value:
              reportExists && status ? (
                <BadgeStatus status={camelCase(status)} size="small" />
              ) : (
                <></>
              ),
          },
          download: {
            variant: 'iconButton',
            iconName: isNotError && reportExists ? 'DownloadIcon' : '',
            color: isSuccessful ? 'primary' : 'disabled',
            onClick: reportExists ? () => handleDownload(row) : undefined,
            disabled: !isSuccessful,
            sx: {
              width: '100%',
            },
          },
          create: {
            value: (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  onClick={() => initializeCreateReport(row)}
                  startIcon={<PlusIcon fontSize="small" />}
                >
                  {formatMessage(messages.create)}
                </Button>
              </Box>
            ),
          },
          viewHistory: {
            value: (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
                onClick={() => openViewHistoryModal(row)}
              >
                <Button variant="text" size="small">
                  {formatMessage(messages.viewHistory)}
                </Button>
              </Box>
            ),
          },
        };
      }),
    [
      sortedAndFilteredResults,
      initializeCreateReport,
      handleDownload,
      openViewHistoryModal,
      formatMessage,
    ],
  );
};

export const useGetFilterOptions = ({ results }) => {
  return useMemo(() => {
    const options = {
      statusOptions: [],
    };

    if (results) {
      results.forEach(({ status }) => {
        if (status) options.statusOptions.push(status);
      });

      options.statusOptions = Array.from(new Set(options.statusOptions))
        .sort()
        .map((reportStatus) => ({
          text: startCase(replace(reportStatus, '_', ' ').toLowerCase()),
          value: reportStatus.toLowerCase(),
        }));
    }

    return options;
  }, [results]);
};

export const useFetchReportsStatuses = ({
  organizationId,
  newlyGeneratedReports,
  setNewlyGeneratedReports,
}) => {
  const formatMessage = useFormatMessage();
  const queryClient = useQueryClient();
  const abortController = new AbortController();
  const queryKey = ['CentralizedReports', 'status'];
  const options = { signal: abortController.signal };
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      reportsService.getCentralizedReportStatus({
        options,
        organizationId,
      }),
    {
      refetchInterval: 5000,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['CentralizedReports', organizationId]);
        data?.forEach((datum) => {
          const reportIsNewlyGenerated = newlyGeneratedReports
            .map((report) => report.id)
            .includes(datum.fortressReportId);

          if (reportIsNewlyGenerated) {
            const reportGenerationIsInProgress = datum.status === IN_PROGRESS;
            const reportGenerationWasSuccessful = datum.status === SUCCESSFUL;
            const reportGenerationFailed = datum.status === ERROR;
            const reportGenerationNoData = datum.status === NO_DATA;

            if (reportGenerationIsInProgress) {
              return;
            }

            if (reportGenerationWasSuccessful) {
              // Invalidate the history query for this specific report
              // so View History shows the newly generated report
              queryClient.invalidateQueries([
                'CentralizedReports',
                'history',
                datum.fortressReportId,
              ]);
              toastr.success(
                formatMessage(messages.success),
                formatMessage(messages.reportCreated),
              );
            } else if (reportGenerationNoData) {
              toastr.info(
                formatMessage(messages.info),
                formatMessage(messages.reportNoDataAvailable),
              );
            } else if (reportGenerationFailed && !datum.failedReports?.length) {
              toastr.error(
                formatMessage(messages.error),
                formatMessage(messages.reportNotFound),
              );
            }

            // `message` is only set for a failure the user can correct in
            // Fortress, so it is shown as written; any other failure keeps
            // the generic text.
            datum.failedReports?.forEach(({ propertyName, message }) =>
              toastr.error(
                formatMessage(messages.error),
                message
                  ? formatMessage(messages.reportPropertyFailedWithReason, {
                      propertyName,
                      message,
                    })
                  : formatMessage(messages.reportPropertyFailed, {
                      propertyName,
                    }),
              ),
            );

            setNewlyGeneratedReports(
              newlyGeneratedReports.filter(
                (r) => r.id !== datum.fortressReportId,
              ),
            );
          }
        });
      },
      onError: () =>
        toastr.error(
          formatMessage(messages.error),
          formatMessage(messages.reportNotFound),
        ),
    },
  );

  return {
    results: useMemo(() => data ?? [], [data]),
    isLoading,
  };
};

const SCHEDULED_REPORTS_PROPERTY_PATH_MAP = {
  report: {
    path: 'reportName',
    dataType: 'string',
  },
  frequency: {
    path: 'frequency',
    dataType: 'string',
  },
  lastRun: {
    path: 'lastRun',
    dataType: 'date',
  },
  levelType: {
    path: 'levelType',
    dataType: 'string',
  },
};

export const useFetchScheduledReports = ({
  organizationId,
  userId,
  enabled = true,
}) => {
  const abortControllerOptions = useAbortController();
  const queryKey = ['ScheduledReports', organizationId, userId];
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      scheduledReportService.listSchedules(
        {
          organizationId,
        },
        abortControllerOptions,
      ),
    {
      enabled: enabled && Boolean(organizationId?.length),
      refetchOnWindowFocus: false,
      refetchInterval: 30000, // Refetch every 30 seconds
    },
  );

  return {
    results: useMemo(() => {
      const items = data?.results || data || [];
      if (!Array.isArray(items)) return [];
      const filteredItems = userId
        ? items.filter((item) => item.createdBy === userId)
        : items;
      return filteredItems.map((item) => {
        const reportVersion = item.reportVersion ?? 'legacy';
        const reportName =
          item.reportName ?? item.report?.name ?? 'Unknown Report';

        const lastRunDate =
          item.lastExecutedAt ?? item.lastRun ?? item.lastExecutionDate;
        const lastRunMoment = toMoment(lastRunDate);
        const lastRun = lastRunMoment?.isValid()
          ? lastRunMoment.toISOString()
          : lastRunDate;

        if (item.frequency && item.frequency !== 'one-time') {
          const baseMoment = lastRunMoment?.isValid()
            ? lastRunMoment.clone()
            : toMoment(new Date());

          if (baseMoment?.isValid()) {
            if (item.frequency === 'daily') {
              baseMoment.add(1, 'day');
            } else if (item.frequency === 'weekly') {
              baseMoment.add(1, 'week');
            } else if (item.frequency === 'monthly') {
              baseMoment.add(1, 'month');
            }
          }
        }

        const levelType =
          item.isPropertyLevel === false ? 'portfolio' : 'property';

        return {
          id: item.id,
          reportId: item.reportId,
          reportName,
          reportVersion,
          frequency: item.frequency,
          lastRun,
          executionTime: item.executionTime,
          timezone: item.timezone ?? 'America/Chicago',
          properties: item.properties ?? [],
          propertyIds: item.propertyIds ?? [],
          targetEmails: item.targetEmails ?? [],
          dateRangeType: item.dateRangeType,
          // leaseType is needed so the edit modal hydrates the Lease Change Out
          // Lease Type dropdown from the saved schedule. ENG-27547.
          leaseType: item.leaseType,
          runByPeriod: item.runByPeriod,
          projectName: item.projectName,
          fileType: item.fileType,
          exportAs: item.exportAs,
          dayOfWeek: item.dayOfWeek,
          dayOfMonth: item.dayOfMonth,
          executionDate: item.executionDate,
          startDate: item.startDate,
          endDate: item.endDate,
          createdBy: item.createdBy,
          createdByUsername: item.createdByUsername,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          organizationId: item.organizationId,
          isActive: item.isActive,
          schedulerRuleArn: item.schedulerRuleArn,
          isPropertyLevel: item.isPropertyLevel,
          levelType,
        };
      });
    }, [data, userId]),
    isLoading,
  };
};

export const useDeleteScheduledReport = () => {
  const formatMessage = useFormatMessage();
  const queryClient = useQueryClient();
  const deleteScheduleMutation = useMutation(
    (id) => {
      const abortController = new AbortController();
      return scheduledReportService.deleteSchedule(id, {
        signal: abortController.signal,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ScheduledReports']);
        toastr.success(
          formatMessage(messages.success),
          formatMessage(
            messages.scheduleDeleted || 'Schedule deleted successfully',
          ),
        );
      },
      onError: (error) => {
        toastr.error(
          formatMessage(messages.error),
          error?.message ||
            formatMessage(
              messages.scheduleDeleteError || 'Failed to delete schedule',
            ),
        );
      },
    },
  );
  return deleteScheduleMutation.mutate;
};

export const useScheduledReportHeaders = () => {
  const formatMessage = useFormatMessage();

  return useMemo(() => {
    const levelTypeFilterOptions = [
      { text: formatMessage(messages.portfolioLevel), value: 'portfolio' },
      { text: formatMessage(messages.propertyLevel), value: 'property' },
    ];

    return [
      {
        id: 'report',
        label: <FormattedMessage {...messages.report} />,
        sortable: true,
        defaultWidth: 280,
        sx: { backgroundColor: 'primary.main' },
        filterOptions: levelTypeFilterOptions,
      },
      {
        id: 'frequency',
        label: <FormattedMessage {...messages.frequency} />,
        sortable: true,
        defaultWidth: 180,
        sx: { backgroundColor: 'primary.main' },
      },
      {
        id: 'lastRun',
        label: <FormattedMessage {...messages.lastRun} />,
        sortable: true,
        defaultWidth: 180,
        sx: { backgroundColor: 'primary.main' },
      },
      {
        id: 'actions',
        label: <FormattedMessage {...messages.actionsSchedule} />,
        defaultWidth: 100,
        sx: { backgroundColor: 'primary.main' },
      },
    ];
  }, [formatMessage]);
};

export const useScheduledReportRows = ({
  sortedAndFilteredResults,
  handleViewEdit,
}) => {
  const formatMessage = useFormatMessage();
  return useMemo(
    () =>
      sortedAndFilteredResults.map((row) => {
        const { reportName, frequency, lastRun, isPropertyLevel, levelType } =
          row;

        // Add "(Portfolio)" prefix for reports with isPropertyLevel === false
        const displayReportName =
          isPropertyLevel === false
            ? `(Portfolio) ${reportName ?? ''}`
            : reportName ?? '';

        return {
          report: {
            value: displayReportName,
          },
          frequency: {
            value: frequency ?? '',
            sx: { textTransform: 'capitalize' },
          },
          lastRun: {
            variant: 'date',
            value: lastRun ?? null,
          },
          actions: {
            variant: 'button',
            value: formatMessage(messages.viewEdit),
            onClick: () => handleViewEdit(row),
          },
          levelType: {
            value: levelType,
          },
        };
      }),
    [sortedAndFilteredResults, handleViewEdit, formatMessage],
  );
};
